import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Button, Hidden, Link } from '@material-ui/core';
import Tooltip from '@material-ui/core/Tooltip';

// CONSTANTS
import { ROUTE_CHANNELS_WITH_ID, ROUTE_CSR_CHANNELS_WITH_ID } from '@/constants/routes';

// UTILITY
import { getLink } from '@/utility/routes';
import { customAnalyticsEvent } from '@/utility/analytics';
import store from '@/utility/store';

// HOC
import { useGuard } from '@/hoc/Guard';

// COMPONENTS
import RouterLink from '@/components/Link';
import ChannelsCarousel, { ChannelsCarouselSlide } from '@/components/Carousels/ChannelsCarousel';

// STYLES
import { ChannelListContainer, Title, ChannelsContainer, ChannelsWrapper, Channel } from './styles';

// HELP
import LessChannelsNotification from '../LessChannelsNotification';

const ChannelList = props => {
  const [t] = useTranslation();
  const params = useParams();
  const isNotPartnerAndIframe = useGuard({ partner: false, iframe: false });
  const {
    type,
    channels,
    onChannelRemove,
    onUnselectAll,
    analyticsLocation,
    channelsCountForAlert,
  } = props;

  const needToShowAlert = channelsCountForAlert ? channels.length >= channelsCountForAlert : false;
  const uniqueIdFromUrl = params.unique_id || params.id;
  const unique_id = uniqueIdFromUrl || store.get('unique_id') || '';
  const isResults = type === 'results' || type === 'csr-results';
  const isSuperBundleResults = type === 'superbundle-results';
  const channelsLink = getLink(
    type === 'csr-results' ? ROUTE_CSR_CHANNELS_WITH_ID : ROUTE_CHANNELS_WITH_ID,
    { unique_id },
  );

  const options = {
    slidesPerView: 'auto',
    watchOverflow: true,
    spaceBetween: 10,
  };

  const handleUnselectClick = event => {
    event.preventDefault();
    onUnselectAll();
  };

  const handleEditChannels = () => {
    customAnalyticsEvent('Edit Channels Link', {
      category: 'Link Clicked',
    });
  };

  return (
    <ChannelListContainer className="channel-list">
      <Hidden smDown>
        <Title type={type}>{t('channels.selectedChannels')}</Title>
      </Hidden>
      {isResults && (
        <>
          <Hidden xsDown>
            <Button
              color="primary"
              size="small"
              component={RouterLink}
              to={channelsLink}
              className="edit-channels-link"
              onClick={handleEditChannels}
              location={analyticsLocation}
            >
              {t('buttons.edit')}
            </Button>
          </Hidden>
          <Hidden smUp>
            <Link
              className="channel-list__link"
              color="primary"
              component={RouterLink}
              to={channelsLink}
              onClick={handleEditChannels}
              location={analyticsLocation}
            >
              {t('buttons.editChannels')}
            </Link>
          </Hidden>
        </>
      )}
      {(type === 'channels' || type === 'only-channels') && channels.length > 1 ? (
        <Link
          className="channel-list__link"
          color="error"
          onClick={handleUnselectClick}
          component="button"
        >
          {t('buttons.unselectAll')}
        </Link>
      ) : null}
      <ChannelsContainer type={type}>
        {isNotPartnerAndIframe && <LessChannelsNotification show={needToShowAlert} />}
        <ChannelsWrapper>
          <ChannelsCarousel options={options}>
            {channels.map(channel => (
              <ChannelsCarouselSlide key={channel.channel_id}>
                <Tooltip title={channel.name}>
                  <Channel
                    className={`channel ${
                      isResults || isSuperBundleResults ? 'channel--read-only' : ''
                    } noselect`}
                    onClick={() => {
                      onChannelRemove(channel);
                    }}
                  >
                    <img
                      src={channel.img || `https://dummyimage.com/64X64?text=${channel.name}`}
                      alt={channel.name}
                    />
                  </Channel>
                </Tooltip>
              </ChannelsCarouselSlide>
            ))}
          </ChannelsCarousel>
        </ChannelsWrapper>
      </ChannelsContainer>
    </ChannelListContainer>
  );
};

ChannelList.defaultProps = {
  analyticsLocation: '',
};

ChannelList.propTypes = {
  type: PropTypes.string.isRequired,
  channels: PropTypes.array.isRequired,
  onChannelRemove: PropTypes.func.isRequired,
  onUnselectAll: PropTypes.func.isRequired,
  analyticsLocation: PropTypes.string,
  channelsCountForAlert: PropTypes.number,
};

export default React.memo(ChannelList);
